import { type Message, TimetokenUtils, type Channel } from '@pubnub/chat';
import { play } from 'cuelume';
import { format } from 'date-fns';
import { ChannelType, MessageStatus } from '@avanta/shared/constants/chat';
import { LOCALES } from '@avanta/shared/constants/common';
import { i18n } from '@avanta/shared/i18n';
import { useChatStore } from '@avanta/shared/stores/chat/index';
import { type OptimisticMessage, type Conversation } from '@avanta/shared/types/chat';

export const uuid = (): string => `temp_${Date.now()}_${Math.random().toString(36).slice(2)}`;

const resolveLastMessageAt = async (channel: Channel): Promise<number | undefined> => {
  try {
    const timetoken = (await channel.getHistory({ count: 1 })).messages[0]?.timetoken;
    if (timetoken) return TimetokenUtils.timetokenToDate(String(timetoken)).getTime();
  } catch {
    // fallback to channel metadata below
  }

  if (channel.updated) return new Date(channel.updated).getTime();

  return Date.now();
};

/**
 * PubNub's raw unread count only looks at `lastReadMessageTimetoken` and doesn't know who
 * sent each message. Real-time listeners (see useRealtimeMessages/useUnreadCounts) exclude
 * self-sent messages so that, e.g., an automatic reply sent under the current account
 * (the psychologist onboarding bot) never counts as unread for that same account. This
 * recomputes the count the same way for the initial load, otherwise a self-sent message
 * left unread would inflate the badge shown right after a reload.
 */
const resolveUnreadCount = async (
  channel: Channel,
  rawCount: number,
  lastReadMessageTimetoken: string | number | boolean | undefined,
  currentUserId: string
): Promise<number> => {
  if (rawCount === 0 || lastReadMessageTimetoken === undefined || lastReadMessageTimetoken === null)
    return rawCount;

  try {
    const { messages } = await channel.getHistory({
      endTimetoken: String(lastReadMessageTimetoken),
      count: rawCount + 5,
    });
    return messages.filter(
      message =>
        String(message.timetoken) > String(lastReadMessageTimetoken) &&
        message.userId !== currentUserId
    ).length;
  } catch {
    return rawCount;
  }
};

export const fetchConversationsFromSDK = async (): Promise<Conversation[]> => {
  const { chat } = useChatStore.getState();
  if (!chat?.currentUser) return [];

  const [{ memberships }, unreadCounts] = await Promise.all([
    chat.currentUser.getMemberships(),
    chat.getUnreadMessagesCounts(),
  ]);

  const directMemberships = memberships.filter(
    membership => membership.channel?.type === ChannelType.DIRECT
  );

  const results = await Promise.all(
    directMemberships.map(async (membership): Promise<Conversation | null> => {
      const channel = membership.channel;
      if (!channel) return null;

      const { members } = await channel.getMembers();
      const otherUser = members.find(member => member.user?.id !== chat.currentUser.id)?.user;
      if (!otherUser) return null;

      const rawUnreadCount = unreadCounts?.find(u => u.channel.id === channel.id)?.count ?? 0;
      const unreadCount = await resolveUnreadCount(
        channel,
        rawUnreadCount,
        membership.lastReadMessageTimetoken,
        chat.currentUser.id
      );

      return {
        channel,
        otherUser,
        membership,
        unreadCount,
        lastMessageAt: await resolveLastMessageAt(channel),
      };
    })
  );

  return results.filter((conversation): conversation is Conversation => conversation !== null);
};

export const toPending = (
  text: string,
  userId: string,
  channelId: string,
  tempId: string
): OptimisticMessage =>
  ({
    tempId,
    clientId: tempId,
    status: MessageStatus.sending,
    text,
    userId,
    channelId,
    timetoken: tempId,
    files: [],
    reactions: {},
    actions: undefined,
    deleted: false,
  }) as unknown as OptimisticMessage;

/**
 * Converts an SDK Message into an OptimisticMessage.
 *
 * DO NOT use spread or Object.assign with Object.create: Object.assign only copies OWN ENUMERABLE
 * properties, and some internal properties of the PubNub SDK (such as `content`, which the `text`
 * getter reads from `this.content.text`) may be non-enumerable. This could result in `text` being undefined.
 *
 * Solution: mutate the original instance by adding `status` as its own property.
 * This preserves ALL getters and methods on the instance intact.
 * Every delivery from the SDK (getHistory / channel.connect) creates fresh instances,
 * so mutation is safe.
 */
export const toSent = (msg: Message): OptimisticMessage => {
  const optimistic = msg as OptimisticMessage;
  optimistic.status = MessageStatus.sent;
  if (!optimistic.clientId) optimistic.clientId = String(msg.timetoken);
  return optimistic;
};

/**
 * Creates a copy of an OptimisticMessage with the given overrides, without mutating the original.
 * This is necessary for optimistic updates with rollback (such as edits, reactions, etc.).
 *
 * For plain objects (pending messages), it uses the spread operator.
 * For SDK instances, it uses Object.getOwnPropertyDescriptors to copy ALL own property descriptors
 * (including enumerable and non-enumerable), then defines the overrides as own properties,
 * shadowing any original getters.
 */
export const withOverrides = (
  msg: OptimisticMessage,
  overrides: Record<string, unknown>
): OptimisticMessage => {
  const proto = Object.getPrototypeOf(msg) as object;
  if (proto === Object.prototype || proto === null) {
    return { ...msg, ...overrides } as OptimisticMessage;
  }
  const clone = Object.create(proto) as OptimisticMessage;
  Object.defineProperties(clone, Object.getOwnPropertyDescriptors(msg));
  for (const [key, value] of Object.entries(overrides)) {
    Object.defineProperty(clone, key, {
      value,
      writable: true,
      configurable: true,
      enumerable: true,
    });
  }
  return clone;
};

export const playNotificationSound = (): void => {
  play('chime');
};

export const determineUserReadableDate = (timetoken: string): string => {
  if (!timetoken || isNaN(Number(timetoken))) return '';
  const date = TimetokenUtils.timetokenToDate(timetoken);
  const language = i18n.language.split('-')[0] as keyof typeof LOCALES;
  const locale = LOCALES[language] ?? LOCALES.en;
  return format(date, 'EEE d MMM HH:mm', { locale });
};

export const cloneFilesForPubNub = (files: File[]): File[] =>
  files.map(file => new File([file], file.name, { type: file.type }));
